/**
 * English strings. This file is the reference: every key that exists here
 * should exist in tr.js, and a missing Turkish key falls back to the value
 * here rather than showing a raw key.
 */

export const en = {
  nav: {
    about: "About",
    experience: "Experience",
    projects: "Projects",
    articles: "Articles",
    contact: "Contact",
    aiResources: "AI Resources",
    changelog: "Changelog",
    testimonials: "Testimonials",
    personalSpace: "Personal Space",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },

  common: {
    backToHome: "Back to home",
    backToArticles: "Back to articles",
    allAiResources: "All AI resources",
    viewAll: "View all",
    readMore: "Read more",
    loading: "Loading",
    minRead: "{{count}} min read",
    notFound: "Not found",
    clearFilter: "Clear filter",
    all: "All",
    groups: "groups",
    tools: "tools",
    otherCategories: "Other categories",
  },

  hero: {
    greeting: "Hi, I'm Alper Kirmizi",
    headline: "Crafting exceptional experiences across the entire Apple ecosystem.",
    bio: "Senior Software Engineer dedicated to building secure, high-performance software within the Apple platforms. Focused on creating reliable fintech experiences at Paycell.",
    ctaRecommendations: "Read Recommendations",
    ctaAiResources: "AI Resources",
    linkedin: "LinkedIn",
    medium: "Medium",
  },

  articles: {
    label: "Articles",
    sectionTitle: "Writing & thoughts",
    pageTitle: "Writing & thoughts",
    pageIntro:
      "Long-form articles about iOS development, architecture decisions, and lessons learned building fintech apps at scale.",
    viewAll: "View all articles",
    count: "{{count}} articles",
    countIn: "{{count}} articles in {{tag}}",
    none: "No articles found for this tag.",
    englishOnly: "This article is in English",
    englishOnlyHint: "The body has not been translated yet.",
    turkishOnly: "This article is in Turkish",
    turkishOnlyHint: "The body has not been translated into English yet.",
    searchPlaceholder: "Search articles",
    startHere: "Start here",
    related: "Related articles",
    onThisPage: "On this page",
  },

  ai: {
    label: "AI Resources",
    pageTitle: "The stack I actually use",
    pageIntro:
      "Not a list of everything that exists. These are the tools that survived contact with a regulated codebase, a release schedule and a limited amount of patience, plus what each one is genuinely good at.",
    summary: "{{categories}} categories · {{tools}} tools",
    categorySummary: "{{groups}} groups · {{tools}} tools",
    whatItIs: "What it is",
    howIUseIt: "How I use it",
    whyItEarns: "Why it earns its place",
    moreIn: "More in {{group}}",
    officialSite: "Official site",
    categoryNotFound: "Category not found",
    categoryNotFoundBody: "That category does not exist.",
    toolNotFound: "Tool not found",
    toolNotFoundBody: "That entry does not exist.",
    status: {
      daily: "Daily driver",
      weekly: "Weekly",
      occasional: "Occasional",
      evaluating: "Evaluating",
    },
  },

  testimonials: {
    label: "Testimonials",
    sectionTitle: "What colleagues say",
    intro:
      "After 6+ years at Paycell and a decade in mobile development, I've had the privilege of working with talented teams across fintech.",
    leaveOne: "Worked together? Leave a recommendation.",
    viewAll: "View all testimonials",
    empty: "No testimonials yet.",
    beFirst: "Be the first to leave a recommendation!",
  },

  contact: {
    label: "Contact",
    sectionTitle: "Let's connect",
    intro:
      "Whether it's consulting, partnership opportunities, mentorship, or event invitations — I'm always open to meaningful conversations.",
    leaveMessage: "Leave a Message",
    linkedinDescription: "Professional network",
    githubDescription: "Open source & code",
    emailDescription: "Get in touch",
  },

  forms: {
    name: "Full Name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    role: "Role & Company",
    send: "Send",
    sending: "Sending…",
    sent: "Sent",
    cancel: "Cancel",
    required: "This field is required",
    invalidEmail: "Enter a valid email address",
    genericError: "Something went wrong. Please try again.",
    successMessage: "Thank you. I'll get back to you.",
  },

  about: {
    label: "About",
    title: "Engineering the",
    titleLine2: "Apple Ecosystem",
    yearsTitle: "6+ Years in Apple Platforms",
    yearsSubtitle: "iOS, macOS & watchOS Development",
    yearsBody:
      "I don't just write code; I architect end-to-end products. Balancing the rigorous engineering demands of high-scale enterprise systems with a deep passion for pixel-perfect UI and seamless user experiences across the entire Apple ecosystem.",
    yearsCurrent:
      "Currently: Exploring dopamine-driven productivity mechanics through personal side-projects, and writing about mobile architecture in the AI era.",
    domainTitle: "Domain Expertise",
    domainBody:
      "Navigating highly regulated, high-scale environments. From orchestrating complex payment flows to developing digital lending and stock trading modules, security and performance are non-negotiable.",
    stackTitle: "Ecosystem & Engineering",
    architectureTitle: "Architecture & Leadership",
    architectureBody:
      "Building systems that scale and teams that thrive. Known for maintaining a calm, positive energy in high-stakes environments, I comfortably take the initiative on bold technical decisions. I mentor peers and guide cross-functional teams to keep both morale and engineering standards exceptionally high.",
    remoteTitle: "The Remote Craft",
    remoteBody:
      "Operating 100% remotely for the past 5 years. While my daily deep-work happens at my base in Edirne with a V60 pour-over, I frequently gather with my teammates in person. I believe true remote discipline requires meticulous asynchronous communication combined with genuine, joyful human connection.",
  },

  experience: {
    label: "Experience",
    title: "Where I work",
    role: "Senior iOS Developer",
    period: "2020 — Present",
    summary:
      "Selected from 63,000 candidates through a rigorous 8-month 'Top Talent' program, I now lead mobile development for this large-scale enterprise fintech platform. Acting as both a technical lead and Scrum Master, I drive bold architecture decisions, App Store release cycles, and team velocity while maintaining hands-on technical excellence.",
    domainsLabel: "Key Projects & Domains",
    ndaNote:
      "Detailed project information is confidential due to NDA agreements. The work involves payment processing, digital wallet solutions, and financial services at enterprise scale.",
    contributionsLabel: "Key Contributions",
  },

  projects: {
    label: "Projects",
    sectionTitle: "Things I have built",
    pageTitle: "Things I have built",
    pageIntro:
      "Products I designed, built and shipped on my own: a macOS clipboard tool, a focus timer for people who do not respond well to shame, and a course app built for one teacher.",
    viewAll: "View all projects",
    none: "No projects on that platform.",
    notFound: "Project not found",
    notFoundBody: "That project does not exist.",
    allProjects: "All projects",
    nextProject: "Next project",
    role: "Role",
    timeline: "Timeline",
    stack: "Stack",
    visitSite: "Visit site",
    noWriteup: "The write-up for this project is not published yet.",
    liveOnSite: "Live on this site",
    tryLiveTitle: "Try it right here",
    tryLiveBody: "This project runs inside this site. Open it and work through a level.",
    openApp: "Open the app",
  },

  footer: {
    builtWith: "Built with React & FastAPI",
    rights: "© 2026 Alper Kirmizi. All rights reserved.",
  },
};
